import { JSX } from 'react';
import { Link, ReferenceField, TextField, useRecordContext } from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';
import { hostUrl } from "../../../utils/url-manager";

/**
 * What this company is, above the fields that say what it holds.
 *
 * The three questions an admin opens a company to answer are whose it is, whether its domains
 * work, and how much is hanging off it. None of them could be answered from the old screen
 * without reading eight cards: the customer account was a `ReferenceField` rendering the bare
 * number `1` among the industry fields, the logo was mapped by nothing, and the campaign
 * collections were not on the screen at all.
 */
const Reading: (props: { label: string, value: string, mono?: boolean }) => JSX.Element =
    (props: { label: string, value: string, mono?: boolean }): JSX.Element => {
        return (
            <Box sx={ { minWidth: '110px' } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow, 9.5px)',
                    letterSpacing: 'var(--wf-track, 0.16em)',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: 'text.secondary',
                } }>
                    { props.label }
                </Typography>
                <Typography sx={ {
                    fontSize: '15px',
                    fontWeight: 600,
                    fontFamily: props.mono === true ? 'var(--wf-font-mono, monospace)' : undefined,
                } }>
                    { props.value }
                </Typography>
            </Box>
        );
    }

export const CompanySummaryStrip: () => JSX.Element = (): JSX.Element => {
    const company: any = useRecordContext();
    const logo: string|undefined = company?.logo;

    return (
        <Box sx={ {
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '18px',
            padding: '12px 14px',
            marginBottom: '6px',
            border: '1px solid',
            borderColor: 'var(--wf-line, rgba(1, 48, 66, 0.13))',
            borderRadius: 1,
            background: 'var(--wf-surface-2, #f1f6f8)',
        } }>
            { (logo !== undefined && logo !== null && logo !== '') &&
                <Box component="img"
                     src={ hostUrl + logo }
                     alt={ (company?.name ?? 'Company') + ' logo' }
                     sx={ { height: '44px', width: 'auto', maxWidth: '120px', objectFit: 'contain' } } />
            }
            <Box>
                <Typography sx={ { fontSize: '18px', fontWeight: 700, lineHeight: 1.2 } }>
                    { company?.name ?? 'Unnamed company' }
                </Typography>
                { (company?.tradingName !== undefined && company?.tradingName !== null
                    && company?.tradingName !== '' && company?.tradingName !== company?.name) &&
                    <Typography sx={ { fontSize: '13px', color: 'text.secondary' } }>
                        { 'Trading as ' + company.tradingName }
                    </Typography>
                }
            </Box>

            <Box sx={ { display: 'flex', flexWrap: 'wrap', gap: '18px', marginLeft: 'auto' } }>
                <Box sx={ { minWidth: '150px' } }>
                    <Typography sx={ {
                        fontSize: 'var(--wf-eyebrow, 9.5px)',
                        letterSpacing: 'var(--wf-track, 0.16em)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        color: 'text.secondary',
                    } }>
                        { 'Customer account' }
                    </Typography>
                    { (company?.customerAccount !== undefined && company?.customerAccount !== null
                        && company?.customerAccount !== '') &&
                        <Box sx={ { fontSize: '15px', fontWeight: 600 } }>
                            <Link to={ '/customer_accounts/' + company.customerAccount }>
                                { 'Account ' + company.customerAccount }
                            </Link>
                        </Box>
                    }
                    { (company?.customerAccount === undefined || company?.customerAccount === null
                        || company?.customerAccount === '') &&
                        <Chip label="CRM only"
                              size="small"
                              variant="outlined"
                              sx={ { marginTop: '2px' } } />
                    }
                </Box>
                <Reading label="Domains" value={ String(company?.websites?.length ?? 0) } mono />
                <Reading label="People" value={ String(company?.employees?.length ?? 0) } mono />
                <Reading label="Campaigns"
                         value={ String((company?.mysteryShoppers?.length ?? 0) + (company?.complianceSniffers?.length ?? 0)) }
                         mono />
                { (company?.companiesHouse !== undefined && company?.companiesHouse !== null
                    && company?.companiesHouse !== '') &&
                    <Box sx={ { minWidth: '150px' } }>
                        <Typography sx={ {
                            fontSize: 'var(--wf-eyebrow, 9.5px)',
                            letterSpacing: 'var(--wf-track, 0.16em)',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            color: 'text.secondary',
                        } }>
                            { 'Companies House' }
                        </Typography>
                        <ReferenceField reference="companies_houses" source="companiesHouse" link="show">
                            <TextField source="companyName" />
                        </ReferenceField>
                    </Box>
                }
            </Box>
        </Box>
    );
}
